import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';

const TypewriterText = ({ text, delay = 0, speed = 30 }) => {
  const [displayed, setDisplayed] = useState('');
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setStarted(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  useEffect(() => {
    if (!started) return;
    let i = 0;
    const interval = setInterval(() => {
      if (i <= text.length) {
        setDisplayed(text.slice(0, i));
        i++;
      } else {
        clearInterval(interval);
      }
    }, speed);
    return () => clearInterval(interval);
  }, [started, text, speed]);

  return (
    <span>
      {displayed}
      {started && displayed.length < text.length && (
        <motion.span
          className="inline-block w-[2px] h-[1em] bg-ai-primary ml-0.5 align-middle"
          animate={{ opacity: [1, 0] }}
          transition={{ duration: 0.5, repeat: Infinity }}
        />
      )}
    </span>
  );
};

const experienceItems = [
  { year: new Date().getFullYear() - 2014 + '+', label: 'YEARS EXP', color: 'text-human-primary' },
  { year: '100%', label: 'UPTIME', color: 'text-order' },
  { year: 'N+', label: 'SYSTEMS', color: 'text-ai-primary' },
];

export const BioSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  return (
    <section
      id="architect"
      ref={sectionRef}
      data-testid="bio-section"
      className="relative py-24 md:py-36 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #050A10 0%, #100C0C 50%, #050A10 100%)' }}
    >
      {/* Noise texture */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
      }} />

      <div className="relative z-10 max-w-6xl mx-auto px-6">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="mb-16"
        >
          <span className="text-ai-text/50 text-xs uppercase tracking-[0.4em] block mb-4">{'// 03'}</span>
          <h2 className="text-2xl md:text-4xl leading-tight">
            <span className="italic text-human-text font-light">The</span>
            {' '}
            <span className="font-bold uppercase text-ai-text tracking-wider">ARCHITECT</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Terminal / ASCII Art side */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-5"
          >
            <div className="border border-ai-secondary/20 bg-ai-bg/60 backdrop-blur-sm">
              {/* Terminal header */}
              <div className="flex items-center gap-2 px-4 py-2 border-b border-ai-secondary/15">
                <div className="w-2 h-2 rounded-full bg-human-primary/60" />
                <div className="w-2 h-2 rounded-full bg-human-secondary/40" />
                <div className="w-2 h-2 rounded-full bg-order/40" />
                <span className="text-ai-text/50 text-xs ml-2 uppercase tracking-widest">whoami.sh</span>
              </div>

              {/* Terminal content */}
              <div className="p-5 md:p-6 space-y-3 text-sm leading-relaxed">
                <div>
                  <span className="text-ai-text/50">$</span>{' '}
                  <span className="text-ai-primary font-semibold">whoami</span>
                </div>
                <div className="text-human-text font-light">
                  {isInView && <TypewriterText text="syamjith_sasidharan" delay={300} speed={50} />}
                </div>

                <div className="pt-2">
                  <span className="text-ai-text/50">$</span>{' '}
                  <span className="text-ai-primary font-semibold">cat</span>{' '}
                  <span className="text-human-secondary">role.txt</span>
                </div>
                <div className="text-human-text/90 font-light italic">
                  {isInView && <TypewriterText text="AI Systems Architect & Product Engineer" delay={1500} speed={30} />}
                </div>

                <div className="pt-2">
                  <span className="text-ai-text/50">$</span>{' '}
                  <span className="text-ai-primary font-semibold">echo</span>{' '}
                  <span className="text-human-secondary">$SPEAKS</span>
                </div>
                <div className="text-ai-text/80 font-semibold uppercase tracking-wider text-xs">
                  {isInView && <TypewriterText text='["Business", "Binary"]' delay={2800} speed={40} />}
                </div>

                {/* Stats */}
                <div className="pt-4 border-t border-ai-secondary/15 mt-4">
                  <div className="grid grid-cols-3 gap-4">
                    {experienceItems.map((item, i) => (
                      <motion.div
                        key={item.label}
                        initial={{ opacity: 0, y: 10 }}
                        animate={isInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ delay: 1 + i * 0.15, duration: 0.5 }}
                        className="text-center"
                      >
                        <div className={`text-2xl font-bold ${item.color}`}>{item.year}</div>
                        <div className="text-xs text-ai-text/70 uppercase tracking-[0.2em] font-medium">{item.label}</div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Bio content side */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="lg:col-span-7 flex flex-col justify-center"
          >
            <h3 className="text-xl md:text-2xl text-human-text font-light italic mb-2">
              Syamjith Sasidharan
            </h3>
            
            <p className="text-xs text-ai-primary uppercase tracking-[0.3em] font-semibold mb-8">
              YOUR HARDLINE TO THE INTELLIGENCE
            </p>

            <p className="text-human-text/90 text-base md:text-lg leading-[2] font-light italic mb-8">
              "I operate as the strategic translator between <span className="text-human-primary">business ambition</span> and <span className="text-ai-primary font-semibold not-italic">artificial intelligence</span>. 
              My expertise lies in converting abstract requirements into production-hardened ecosystems. 
              While AI accelerates the code, I govern the architecture—ensuring every deployment is strictly optimized for 
              <span className="text-ai-primary font-mono not-italic"> security, cost-efficiency, and high resilience</span>. 
              I bring the structural discipline necessary to turn generative speed into lasting value."
            </p>

            {/* Tech stack indicators */}
            <div className="space-y-3">
              <span className="text-xs text-ai-text/50 uppercase tracking-[0.3em] block mb-3">core_stack:</span>
              <div className="flex flex-wrap gap-2">
                {['Go', 'Rust','Cloud','Architecture','AI','ML','DevOps', 'Node.js', 'AWS', 'Docker', 'Cloudflare', 'MongoDB', 'PostgreSQL', 'React', 'Next.js'].map((tech, i) => (
                  <motion.span
                    key={tech}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={isInView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ delay: 0.8 + i * 0.05, duration: 0.3 }}
                    className="text-xs text-ai-text/70 border border-ai-secondary/30 px-3 py-1 uppercase tracking-wider hover:border-ai-primary/40 hover:text-ai-primary transition-colors duration-300 cursor-default"
                  >
                    {tech}
                  </motion.span>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
